import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import colors from '../colors';
import config from '../config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { IonIcon } from '@ionic/react';
import { chevronDownOutline, chevronUpOutline, downloadOutline } from 'ionicons/icons';
import { 
  IonButton, 
  IonContent, 
  IonItem, 
  IonLabel, 
  IonTextarea, 
  IonPage,
  IonSelect,
  IonSelectOption,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  IonInput,
  IonBadge,
  IonSpinner,
  IonModal,
  IonAlert,
  IonHeader,
  IonToolbar,
  IonTitle
} from '@ionic/react';
import './DietGenerator.css';

const DietGenerator = ({ member_id, user_id, auth_token, noPageWrapper = false }) => {
  // State for auth data
  const [effectiveMemberId, setEffectiveMemberId] = useState(member_id || localStorage.getItem('member_id') || null);
  const [effectiveUserId, setEffectiveUserId] = useState(user_id || localStorage.getItem('user_id') || null);
  const [effectiveAuthToken, setEffectiveAuthToken] = useState(auth_token || localStorage.getItem('auth_token') || null);

  // Sync auth props to state when they change
  useEffect(() => {
    if (member_id) {
      setEffectiveMemberId(member_id);
    }
    if (user_id) {
      setEffectiveUserId(user_id);
    }
    if (auth_token) {
      setEffectiveAuthToken(auth_token);
    }
  }, [member_id, user_id, auth_token]);

  // Track previous member_id to detect changes
  const previousMemberIdRef = useRef(null);

  // Reset form and data when member changes
  useEffect(() => {
    // Check if this is a different member (member changed)
    if (effectiveMemberId && previousMemberIdRef.current && previousMemberIdRef.current !== effectiveMemberId) {
      // Member changed - reset all form fields and data
      setDietaryRestrictions('');
      setAlcoholAllowed('No alcohol');
      setEmailToMember(true);
      setDietType('');
      setCustomDietType('');
      setDailyCalorieTarget('');
      setDietPlan(null);
      setMemberFullName('');
      setPromptsUsed({ system: '', user: '' });
      setShowPrompts(false);
      setAvailablePlans([]);
      setSelectedPlanId('new');
    }
    // Update ref for next comparison
    if (effectiveMemberId) {
      previousMemberIdRef.current = effectiveMemberId;
    }
  }, [effectiveMemberId]);

  // Form state
  const [dietaryRestrictions, setDietaryRestrictions] = useState('');
  const [alcoholAllowed, setAlcoholAllowed] = useState('No alcohol');
  const [emailToMember, setEmailToMember] = useState(true);
  const [dietType, setDietType] = useState('');
  const [customDietType, setCustomDietType] = useState('');
  const [dailyCalorieTarget, setDailyCalorieTarget] = useState('');
  const [memberFullName, setMemberFullName] = useState('');
  
  // Data state
  const [dietPlan, setDietPlan] = useState(null);
  
  // UI state
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const [promptsUsed, setPromptsUsed] = useState({ system: '', user: '' });
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('new');
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isLoadingPlanDetails, setIsLoadingPlanDetails] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Ref for PDF printing
  const printRef = useRef();

  useEffect(() => {
    const fetchMemberName = async () => {
      if (!effectiveMemberId || !effectiveAuthToken) {
        setMemberFullName('');
        return;
      }

      try {
        const response = await fetch(`${config.apiUrl}/members_names`, {
          headers: {
            'Authorization': `Bearer ${effectiveAuthToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          setMemberFullName('');
          return;
        }

        const data = await response.json();
        const members = Array.isArray(data) ? data : [];
        const matchedMember = members.find((member) => String(member.id) === String(effectiveMemberId));
        if (!matchedMember) {
          setMemberFullName('');
          return;
        }

        const fullName = `${matchedMember.First_name || ''} ${matchedMember.Surname || ''}`.trim();
        setMemberFullName(fullName);
      } catch (error) {
        console.error('Error fetching member name:', error);
        setMemberFullName('');
      }
    };

    fetchMemberName();
  }, [effectiveMemberId, effectiveAuthToken]);

  const formatPlanDate = (plan) => {
    const createdAt = plan?.created_at ? new Date(plan.created_at) : null;
    return createdAt
      ? createdAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : 'Unknown date';
  };

  const planLabels = useMemo(() => {
    const countsByDate = {};
    const labelsById = {};

    availablePlans.forEach((plan) => {
      const dateLabel = formatPlanDate(plan);
      const count = (countsByDate[dateLabel] || 0) + 1;
      countsByDate[dateLabel] = count;
      const suffix = count > 1 ? ` (${count})` : '';
      labelsById[plan.id] = `Plan generated on ${dateLabel}${suffix}`;
    });

    return labelsById;
  }, [availablePlans]);

  const normalizeDietPlan = (plan, options = {}) => {
    const { allowEatingWindow = true } = options;

    if (!plan || !Array.isArray(plan.Days)) {
      return null;
    }

    const validatedPlan = plan.Days.map((day, index) => {
      const meals = Array.isArray(day.Meals) ? day.Meals : [];
      const validatedMeals = meals.map((meal) => ({
        Meal: meal.Meal || 'Meal',
        SampleMeal: meal.SampleMeal || '',
        Calories: typeof meal.Calories === 'number' ? meal.Calories : 0,
        Protein_g: typeof meal.Protein_g === 'number' ? meal.Protein_g : 0,
        Carbs_g: typeof meal.Carbs_g === 'number' ? meal.Carbs_g : 0,
        Fat_g: typeof meal.Fat_g === 'number' ? meal.Fat_g : 0,
        Fiber_g: typeof meal.Fiber_g === 'number' ? meal.Fiber_g : 0
      }));

      const dailyTotals = day.DailyTotals || {};
      const validatedDailyTotals = {
        Calories: typeof dailyTotals.Calories === 'number' ? dailyTotals.Calories : 0,
        Protein_g: typeof dailyTotals.Protein_g === 'number' ? dailyTotals.Protein_g : 0,
        Carbs_g: typeof dailyTotals.Carbs_g === 'number' ? dailyTotals.Carbs_g : 0,
        Fat_g: typeof dailyTotals.Fat_g === 'number' ? dailyTotals.Fat_g : 0,
        Fiber_g: typeof dailyTotals.Fiber_g === 'number' ? dailyTotals.Fiber_g : 0
      };

      return {
        Day: day.Day || `Day ${index + 1}`,
        Overview: day.Overview || '',
        Meals: validatedMeals,
        DailyTotals: validatedDailyTotals,
        EatingWindow: allowEatingWindow && day.EatingWindow ? day.EatingWindow : undefined
      };
    });

    return {
      PlanDescription: plan.PlanDescription || 'Your personalised 14-day diet plan has been designed to help you achieve your goals.',
      Days: validatedPlan
    };
  };

  const extractDietPlanFromResponse = (data) => {
    if (!data) return null;
    if (data.plan) return data.plan;
    if (data.diet_plan) return data.diet_plan;
    if (data.dietPlan) return data.dietPlan;
    if (Array.isArray(data.Days) || data.PlanDescription) return data;
    return null;
  };

  const applyDietFormValues = (data) => {
    const dietTypeValue = data?.diet_type || data?.dietType;
    const normalizedDietTypeValue = typeof dietTypeValue === 'string' ? dietTypeValue.trim() : '';
    if (normalizedDietTypeValue) {
      const knownDietTypes = ['Mediterranean', 'Keto', 'Intermittent Fasting', 'Diabetic'];
      if (knownDietTypes.includes(normalizedDietTypeValue)) {
        setDietType(normalizedDietTypeValue);
        setCustomDietType('');
      } else if (normalizedDietTypeValue === 'Custom') {
        setDietType('Custom');
        setCustomDietType('');
      } else {
        setDietType('Custom');
        setCustomDietType(normalizedDietTypeValue);
      }
    } else {
      setDietType('');
      setCustomDietType('');
    }

    const dailyCalories = data?.daily_calorie_target ?? data?.dailyCalorieTarget;
    if (dailyCalories !== undefined && dailyCalories !== null) {
      setDailyCalorieTarget(String(dailyCalories));
    }

    const restrictionsValue = data?.dietary_restrictions ?? data?.dietaryRestrictions;
    if (typeof restrictionsValue === 'string') {
      setDietaryRestrictions(restrictionsValue);
    }

    const alcoholValue = data?.alcohol_allowed ?? data?.alcoholAllowed;
    const alcoholOptions = [
      'No alcohol',
      'Once a week',
      'A few times a week',
      'Once a day (limited)'
    ];
    if (typeof alcoholValue === 'boolean') {
      setAlcoholAllowed(alcoholValue ? 'Once a week' : 'No alcohol');
    } else if (typeof alcoholValue === 'string') {
      const normalizedAlcoholValue = alcoholValue.trim();
      if (normalizedAlcoholValue === 'yes') {
        setAlcoholAllowed('Once a week');
      } else if (normalizedAlcoholValue === 'no') {
        setAlcoholAllowed('No alcohol');
      } else if (alcoholOptions.includes(normalizedAlcoholValue)) {
        setAlcoholAllowed(normalizedAlcoholValue);
      }
    }

    const emailToMemberValue = data?.email_to_member ?? data?.emailToMember;
    if (typeof emailToMemberValue === 'boolean') {
      setEmailToMember(emailToMemberValue);
    } else if (typeof emailToMemberValue === 'string') {
      const normalizedEmailValue = emailToMemberValue.trim().toLowerCase();
      if (normalizedEmailValue === 'true') {
        setEmailToMember(true);
      } else if (normalizedEmailValue === 'false') {
        setEmailToMember(false);
      }
    }
  };

  // PDF print handler - creates a new window with all content to bypass Ionic scroll constraints
  const handlePrint = () => {
    if (!dietPlan) {
      toast.error('No content to print');
      return;
    }

    // Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      toast.error('Please allow popups to print the diet plan');
      return;
    }

    // Get the color values from the colors object
    const colorValues = {
      blueGray: colors.blueGray,
      darkGray: colors.darkGray,
      warmGray: colors.warmGray,
      darkGreen: colors.darkGreen,
      lightPurpleGray: colors.lightPurpleGray
    };

    // Build HTML content from the diet plan data
    const buildPlanHTML = () => {
      let html = `
        <div class="print-header">
          <img src="/infinity-logo.png" alt="Infinity Medical Concierge" class="company-logo" onerror="this.style.display='none'">
        </div>
        <h2>Your 14-Day Diet Plan</h2>
        ${memberFullName ? `<div class="member-name">${memberFullName}</div>` : ''}
        <div class="ai-disclaimer">
          <strong>Important Notice:</strong> This diet plan was generated by artificial intelligence (AI). 
          If you experience any adverse reactions, allergies, or health concerns related to the suggested foods, 
          <strong>stop immediately</strong> and contact Infinity Medical Concierge for medical guidance.
          Infinity Medical Concierge can also refer the Member to a dietician to craft a personalised eating plan.
        </div>
      `;
      
      // Plan Description
      if (dietPlan.PlanDescription) {
        html += `
          <div class="plan-description">
            <h3>About Your Plan</h3>
            <p>${dietPlan.PlanDescription.replace(/\n/g, '<br>')}</p>
          </div>
        `;
      }
      
      // Days
      const days = dietPlan.Days || (Array.isArray(dietPlan) ? dietPlan : []);
      html += '<div class="plan-days">';
      
      days.forEach((day, index) => {
        html += `
          <div class="day-card">
            <div class="day-card-header">
              <div class="day-card-title">${day.Day || `Day ${index + 1}`}: ${day.Overview || ''}</div>
              ${day.EatingWindow ? `<div class="eating-window">Eating Window: ${day.EatingWindow}</div>` : ''}
        `;
        
        // Daily Totals
        if (day.DailyTotals) {
          html += `
            <div class="daily-totals">
              <span class="badge">${day.DailyTotals.Calories || 0} kcal</span>
              <span class="badge badge-secondary">Protein: ${day.DailyTotals.Protein_g || 0}g</span>
              <span class="badge badge-secondary">Carbs: ${day.DailyTotals.Carbs_g || 0}g</span>
              <span class="badge badge-secondary">Fat: ${day.DailyTotals.Fat_g || 0}g</span>
              <span class="badge badge-secondary">Fibre: ${day.DailyTotals.Fiber_g || 0}g</span>
            </div>
          `;
        }
        
        html += `
            </div>
            <div class="day-meals">
        `;
        
        // Meals
        if (day.Meals && Array.isArray(day.Meals) && day.Meals.length > 0) {
          day.Meals.forEach((meal, mealIndex) => {
            html += `
              <div class="meal-card">
                <div class="meal-header">
                  <span class="meal-name">${meal.Meal || 'Meal'}</span>
                  <div class="meal-macros">
                    <span class="badge badge-small">${meal.Calories || 0} kcal</span>
                    <span class="badge badge-small">P: ${meal.Protein_g || 0}g</span>
                    <span class="badge badge-small">C: ${meal.Carbs_g || 0}g</span>
                    <span class="badge badge-small">F: ${meal.Fat_g || 0}g</span>
                    <span class="badge badge-small">Fibre: ${meal.Fiber_g || 0}g</span>
                  </div>
                </div>
                <div class="meal-description">${(meal.SampleMeal || '').replace(/\n/g, '<br>')}</div>
              </div>
            `;
          });
        }
        
        html += `
            </div>
          </div>
        `;
      });
      
      html += '</div>';
      return html;
    };

    // Write the HTML content with embedded styles
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Diet Plan - 14 Day Program</title>
          <meta charset="utf-8">
          <style>
            @page {
              margin: 20mm;
              size: A4;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              font-size: 14px;
              line-height: 1.6;
              color: #333;
              padding: 20px;
              background: white;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .print-header {
              margin-bottom: 30px;
              padding-bottom: 60px;
              border-bottom: 2px solid ${colorValues.blueGray};
              text-align: center;
            }
            .company-logo {
              max-width: 300px;
              max-height: 100px;
              margin-bottom: 30px;
              object-fit: contain;
            }
            .ai-disclaimer {
              background: #fff3cd;
              border: 2px solid #ffc107;
              border-left: 4px solid #ff9800;
              border-radius: 8px;
              padding: 16px;
              margin-bottom: 24px;
              font-size: 13px;
              line-height: 1.7;
              color: #856404;
            }
            .ai-disclaimer strong {
              color: #d32f2f;
              font-weight: 600;
            }
            h2 {
              color: ${colorValues.darkGray};
              font-size: 24px;
              margin-top: 40px;
              margin-bottom: 24px;
            }
            .member-name {
              font-size: 14px;
              color: ${colorValues.warmGray};
              margin-top: -16px;
              margin-bottom: 24px;
              text-align: center;
            }
            h3 {
              color: ${colorValues.darkGray};
              font-size: 18px;
              margin-bottom: 12px;
            }
            .plan-days {
              display: block;
            }
            .day-card {
              page-break-inside: avoid;
              break-inside: avoid;
              margin-bottom: 20px;
              border: 1px solid #ddd;
              border-radius: 8px;
              padding: 16px;
              background: white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .day-card-header {
              margin-bottom: 16px;
              padding-bottom: 12px;
              border-bottom: 1px solid #eee;
            }
            .day-card-title {
              font-size: 18px;
              font-weight: 600;
              margin-bottom: 8px;
              color: ${colorValues.darkGray};
            }
            .eating-window {
              font-size: 13px;
              color: ${colorValues.warmGray};
              font-style: italic;
              margin-top: 4px;
            }
            .daily-totals {
              margin-top: 8px;
            }
            .day-meals {
              margin-top: 16px;
            }
            .meal-card {
              margin-bottom: 16px;
              padding: 12px;
              background: ${colorValues.lightPurpleGray}15;
              border-radius: 8px;
              border-left: 4px solid ${colorValues.blueGray};
            }
            .meal-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 8px;
              flex-wrap: wrap;
              gap: 8px;
            }
            .meal-name {
              font-size: 16px;
              font-weight: 600;
              color: ${colorValues.darkGray};
            }
            .meal-macros {
              display: flex;
              gap: 4px;
              flex-wrap: wrap;
            }
            .meal-description {
              color: #333;
              white-space: pre-wrap;
              line-height: 1.6;
            }
            .badge {
              display: inline-block;
              padding: 4px 8px;
              border-radius: 4px;
              margin-right: 4px;
              font-size: 12px;
              font-weight: 500;
              background: ${colorValues.blueGray};
              color: white;
            }
            .badge-secondary {
              background: ${colorValues.warmGray};
            }
            .badge-small {
              font-size: 11px;
              padding: 2px 6px;
            }
            .plan-description {
              margin-bottom: 24px;
              padding: 16px;
              background: #f5f5f5;
              border-radius: 8px;
              border-left: 4px solid ${colorValues.darkGreen};
            }
            .plan-description p {
              line-height: 1.8;
              margin-top: 8px;
            }
            @media print {
              body {
                padding: 0;
              }
              .print-header {
                page-break-after: avoid;
                break-after: avoid;
              }
              h2 {
                margin-top: 40px;
              }
              .day-card {
                page-break-inside: avoid;
                break-inside: avoid;
                box-shadow: none;
                border: 1px solid #ddd;
              }
              .ai-disclaimer {
                page-break-after: avoid;
                break-after: avoid;
              }
            }
          </style>
        </head>
        <body>
          ${buildPlanHTML()}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              }, 250);
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  // Track effective auth data (from props or localStorage)
  useEffect(() => {
    if (member_id && user_id && auth_token) {
      localStorage.setItem('member_id', member_id);
      localStorage.setItem('user_id', user_id);
      localStorage.setItem('auth_token', auth_token);
      setEffectiveMemberId(member_id);
      setEffectiveAuthToken(auth_token);
    }
  }, [member_id, user_id, auth_token]);

  const fetchExistingPlans = useCallback(async () => {
    if (!effectiveMemberId || !effectiveAuthToken) {
      return;
    }

    setIsLoadingPlans(true);
    try {
      const url = `https://xuar-e6vh-cmcf.e2.xano.io/api:CpKwGPIS/diet_plans/get/${effectiveMemberId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${effectiveAuthToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        toast.error('Failed to load existing diet plans');
        return;
      }

      const data = await response.json();
      const plans = Array.isArray(data) ? data : [];
      plans.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
      setAvailablePlans(plans);
    } catch (error) {
      console.error('Error fetching diet plans:', error);
      toast.error('Failed to load existing diet plans');
    } finally {
      setIsLoadingPlans(false);
    }
  }, [effectiveMemberId, effectiveAuthToken]);

  useEffect(() => {
    fetchExistingPlans();
    setSelectedPlanId('new');
    setDietPlan(null);
  }, [fetchExistingPlans]);

  const handlePlanSelection = async (planId) => {
    setSelectedPlanId(planId);

    if (planId === 'new') {
      setDietPlan(null);
      setPromptsUsed({ system: '', user: '' });
      setShowPrompts(false);
      setDietaryRestrictions('');
      setAlcoholAllowed('No alcohol');
      setEmailToMember(true);
      setDietType('');
      setCustomDietType('');
      setDailyCalorieTarget('');
      return;
    }

    if (!effectiveAuthToken) {
      toast.error('Missing authentication data');
      return;
    }

    setIsLoadingPlanDetails(true);
    try {
      const url = `https://xuar-e6vh-cmcf.e2.xano.io/api:CpKwGPIS/diet_plans/get_plan/${planId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${effectiveAuthToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        toast.error('Failed to load diet plan details');
        return;
      }

      const data = await response.json();
      const planPayload = extractDietPlanFromResponse(data);
      const normalizedPlan = normalizeDietPlan(planPayload, { allowEatingWindow: true });

      if (!normalizedPlan) {
        toast.error('Invalid diet plan structure received');
        return;
      }

      setDietPlan(normalizedPlan);
      applyDietFormValues(data?.inputs ?? data);

      if (data.prompts) {
        setPromptsUsed(data.prompts);
      } else {
        setPromptsUsed({ system: '', user: '' });
      }
      setShowPrompts(false);
    } catch (error) {
      console.error('Error loading diet plan:', error);
      toast.error('Failed to load diet plan details');
    } finally {
      setIsLoadingPlanDetails(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!selectedPlanId || selectedPlanId === 'new') {
      return;
    }

    if (!effectiveAuthToken) {
      toast.error('Missing authentication data');
      return;
    }

    try {
      const url = `https://xuar-e6vh-cmcf.e2.xano.io/api:CpKwGPIS/diet_plans/delete/${selectedPlanId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${effectiveAuthToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        toast.error('Failed to delete diet plan');
        return;
      }

      toast.success('Diet plan deleted');
      setSelectedPlanId('new');
      setDietPlan(null);
      setPromptsUsed({ system: '', user: '' });
      setShowPrompts(false);
      setDietaryRestrictions('');
      setAlcoholAllowed('No alcohol');
      setEmailToMember(true);
      setDietType('');
      setCustomDietType('');
      setDailyCalorieTarget('');
      fetchExistingPlans();
    } catch (error) {
      console.error('Error deleting diet plan:', error);
      toast.error('Failed to delete diet plan');
    }
  };

  // Generate diet plan
  const handleGeneratePlan = async () => {
    const normalizedDietType = dietType === 'Custom' ? customDietType.trim() : dietType;
    if (!normalizedDietType) {
      toast.error('Please select a diet type');
      return;
    }

    const normalizedDailyCalorieTarget =
      dailyCalorieTarget === '' || dailyCalorieTarget === null || dailyCalorieTarget === undefined
        ? null
        : Number(dailyCalorieTarget);
    const dailyCalorieTargetPayload =
      normalizedDailyCalorieTarget === null ? null : parseInt(dailyCalorieTarget, 10);

    if (
      normalizedDailyCalorieTarget !== null
      && (Number.isNaN(normalizedDailyCalorieTarget) || normalizedDailyCalorieTarget <= 0)
    ) {
      toast.error('Please enter a valid daily calorie target (greater than 0)');
      return;
    }

    if (!effectiveMemberId || !effectiveAuthToken || !effectiveUserId) {
      toast.error('Missing authentication data');
      return;
    }

    setSelectedPlanId('new');
    // Show modal before starting generation
    setShowGenerationModal(true);
    setIsGeneratingPlan(true);
    
    // Create AbortController for timeout (declared outside try for cleanup)
    const controller = new AbortController();
    let timeoutId = null;
    
    try {
      // Call backend API with 15-minute timeout
      const url = 'https://feedback-app-367524407367.europe-west2.run.app/diet_plan';
      //const url = 'http://localhost:8000/diet_plan';
      
      // Set timeout
      timeoutId = setTimeout(() => controller.abort(), 15 * 60 * 1000); // 15 minutes
      
      let response;
      try {
        response = await fetch(url, { 
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            auth_token: effectiveAuthToken,
            member_id: effectiveMemberId,
            user_id: effectiveUserId,
            diet_type: normalizedDietType,
            daily_calorie_target: dailyCalorieTargetPayload,
            dietary_restrictions: dietaryRestrictions,
            alcohol_allowed: alcoholAllowed,
            email_to_member: emailToMember
          }),
          signal: controller.signal
        });
        
        // Clear timeout if request completes successfully
        clearTimeout(timeoutId);
      } catch (fetchError) {
        // Clear timeout on fetch error
        clearTimeout(timeoutId);
        
        // Re-throw to be handled by outer catch block
        throw fetchError;
      }

      // Handle 504 Gateway Timeout specifically
      if (response.status === 504) {
        toast.error('The server is taking longer than expected to generate your plan. The plan will be emailed to you when it\'s ready. Please check your email in a few minutes.');
        setIsGeneratingPlan(false);
        setShowGenerationModal(false);
        return;
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ 
          error: `Server error (${response.status}): ${response.statusText || 'Failed to generate diet plan'}` 
        }));
        toast.error(error.error || 'Failed to generate diet plan');
        setIsGeneratingPlan(false);
        setShowGenerationModal(false);
        return;
      }

      const data = await response.json();
      const plan = data.plan; // Contains PlanDescription and Days array

      // Validate the plan structure
      if (!plan || !plan.Days || !Array.isArray(plan.Days)) {
        toast.error('Invalid plan structure received from server');
        setIsGeneratingPlan(false);
        setShowGenerationModal(false);
        return;
      }

      if (plan.Days.length !== 14) {
        toast.error(`Expected 14 days, got ${plan.Days.length}`);
      }

      const normalizedPlan = normalizeDietPlan(plan, { allowEatingWindow: normalizedDietType === 'Intermittent Fasting' });

      if (!normalizedPlan) {
        toast.error('Invalid plan structure received from server');
        setIsGeneratingPlan(false);
        setShowGenerationModal(false);
        return;
      }

      setDietPlan(normalizedPlan);
      
      // Store prompts if returned by backend
      if (data.prompts) {
        setPromptsUsed(data.prompts);
      } else {
        setPromptsUsed({ system: '', user: '' });
      }
      
      toast.success('Diet plan generated successfully!');
      setShowGenerationModal(false);
      fetchExistingPlans();
    } catch (error) {
      console.error('Error generating diet plan:', error);
      
      // Clear timeout if it exists
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      if (error.name === 'AbortError') {
        toast.error('Request timed out. The plan generation is taking longer than expected. The plan will be emailed to you when it\'s ready. Please check your email in a few minutes.');
      } else if (error.message === 'Failed to fetch' || error.message.includes('network') || error.message.includes('NetworkError')) {
        // Network errors often indicate gateway timeouts or connection issues
        toast.error('Connection error. The plan generation may still be processing. The plan will be emailed to you when it\'s ready. Please check your email in a few minutes.');
      } else {
        toast.error(`Failed to generate diet plan: ${error.message || 'Unknown error occurred'}`);
      }
      setShowGenerationModal(false);
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  const content = (
    <div className="diet-plan-container">
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Diet Plan Generator</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {/* Form Section */}
              <div className="form-section">
                {/* Existing Plans */}
                {availablePlans.length > 0 && (
                  <>
                    <IonItem>
                      <IonLabel position="stacked">
                        Existing Plans
                        {isLoadingPlans && (
                          <span style={{ fontSize: '12px', color: colors.warmGray, marginLeft: '8px' }}>
                            (Loading...)
                          </span>
                        )}
                      </IonLabel>
                      <IonSelect
                        value={selectedPlanId}
                        placeholder="Create new plan"
                        onIonChange={(e) => handlePlanSelection(e.detail.value)}
                      >
                        <IonSelectOption value="new">Create new plan</IonSelectOption>
                        {availablePlans.map((plan) => (
                          <IonSelectOption key={plan.id} value={plan.id}>
                            {planLabels[plan.id] || `Plan generated on ${formatPlanDate(plan)}`}
                          </IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>
                    {selectedPlanId !== 'new' && (
                      <IonButton
                        color="danger"
                        fill="outline"
                        style={{ marginTop: '12px' }}
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={isLoadingPlanDetails}
                      >
                        Delete Selected Plan
                      </IonButton>
                    )}
                    {isLoadingPlanDetails && (
                      <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px', color: colors.warmGray }}>
                        <IonSpinner name="crescent" style={{ marginRight: '8px' }} />
                        Loading plan details...
                      </div>
                    )}
                  </>
                )}

                {/* Diet Type */}
                <IonItem>
                  <IonLabel position="stacked">Diet Type *</IonLabel>
                  <IonSelect
                    value={dietType}
                    placeholder="Select your diet type"
                    onIonChange={(e) => {
                      const nextDietType = e.detail.value;
                      setDietType(nextDietType);
                      if (nextDietType !== 'Custom') {
                        setCustomDietType('');
                      }
                    }}
                  >
                    <IonSelectOption value="Mediterranean">Mediterranean</IonSelectOption>
                    <IonSelectOption value="Keto">Keto</IonSelectOption>
                    <IonSelectOption value="Intermittent Fasting">Intermittent Fasting</IonSelectOption>
                    <IonSelectOption value="Diabetic">Diabetic</IonSelectOption>
                    <IonSelectOption value="Custom">Custom</IonSelectOption>
                  </IonSelect>
                </IonItem>
                {dietType === 'Custom' && (
                  <IonItem>
                    <IonLabel position="stacked">Custom Diet Type *</IonLabel>
                    <IonInput
                      value={customDietType}
                      placeholder="e.g., Low FODMAP"
                      onIonInput={(e) => setCustomDietType(e.detail.value ?? '')}
                    />
                  </IonItem>
                )}

                {/* Daily Calorie Target */}
                <IonItem>
                <IonLabel position="stacked">Daily Calorie Target (kcal, optional)</IonLabel>
                  <IonInput
                    type="number"
                    value={dailyCalorieTarget}
                    placeholder="e.g., 2000"
                  onIonInput={(e) => setDailyCalorieTarget(e.detail.value ?? '')}
                  />
                </IonItem>

                {/* Dietary Restrictions */}
                <IonItem>
                  <IonLabel position="stacked">Dietary Restrictions</IonLabel>
                  <IonTextarea
                    value={dietaryRestrictions}
                    placeholder="e.g., lactose-free, halaal, nut allergy"
                    rows={3}
                    onIonInput={(e) => setDietaryRestrictions(e.detail.value)}
                  />
                </IonItem>

                {/* Alcohol Allowed */}
                <IonItem>
                  <IonLabel position="stacked">Allow Alcohol</IonLabel>
                  <IonSelect
                    value={alcoholAllowed}
                    placeholder="Select alcohol preference"
                    onIonChange={(e) => setAlcoholAllowed(e.detail.value)}
                  >
                    <IonSelectOption value="No alcohol">No alcohol</IonSelectOption>
                    <IonSelectOption value="Once a week">Once a week</IonSelectOption>
                    <IonSelectOption value="A few times a week">A few times a week</IonSelectOption>
                    <IonSelectOption value="Once a day (limited)">Once a day (limited)</IonSelectOption>
                  </IonSelect>
                </IonItem>

                <IonItem lines="none">
                  <IonLabel>Do you want to automatically email the report to the Member when it's ready?</IonLabel>
                  <IonCheckbox
                    slot="end"
                    checked={emailToMember}
                    onIonChange={(e) => setEmailToMember(e.detail.checked)}
                  />
                </IonItem>

                {/* Generate Button */}
                <IonButton
                  expand="block"
                  onClick={handleGeneratePlan}
                  disabled={isGeneratingPlan || !dietType || (dietType === 'Custom' && !customDietType.trim())}
                  style={{ marginTop: '16px' }}
                >
                  {isGeneratingPlan ? (
                    <>
                      <IonSpinner name="crescent" style={{ marginRight: '8px' }} />
                      Generating Plan...
                    </>
                  ) : (
                    'Generate Diet Plan'
                  )}
                </IonButton>
              </div>

              {/* Results Section */}
              {dietPlan && (
                <div className="results-section" style={{ marginTop: '32px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                    <div>
                      <h2 style={{ margin: 0 }}>Your 14-Day Diet Plan</h2>
                      {memberFullName && (
                        <div style={{ marginTop: '4px', fontSize: '14px', color: colors.warmGray }}>
                          {memberFullName}
                        </div>
                      )}
                    </div>
                    <IonButton
                      onClick={handlePrint}
                      color="primary"
                      fill="solid"
                    >
                      <IonIcon icon={downloadOutline} slot="start" />
                      Save as PDF
                    </IonButton>
                  </div>

                  <div style={{
                    background: '#fff3cd',
                    border: '2px solid #ffc107',
                    borderLeft: '4px solid #ff9800',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px',
                    fontSize: '13px',
                    lineHeight: '1.7',
                    color: '#856404'
                  }}>
                    <strong>Important Notice:</strong> This diet plan was generated by artificial intelligence (AI). If you experience any adverse reactions, allergies, or health concerns related to the suggested foods, <strong>stop immediately</strong> and contact Infinity Medical Concierge for medical guidance. Infinity Medical Concierge can also refer the Member to a dietician to craft a personalised eating plan.
                  </div>
                  
                  <div ref={printRef}>
                  
                  {/* Plan Description */}
                  {dietPlan.PlanDescription && (
                    <IonCard style={{ marginBottom: '24px' }}>
                      <IonCardHeader>
                        <IonCardTitle>About Your Plan</IonCardTitle>
                      </IonCardHeader>
                      <IonCardContent>
                        <p style={{ lineHeight: '1.8', fontSize: '15px', color: colors.darkGray }}>
                          {dietPlan.PlanDescription}
                        </p>
                      </IonCardContent>
                    </IonCard>
                  )}
                  
                  <div className="plan-days">
                    {(dietPlan.Days || (Array.isArray(dietPlan) ? dietPlan : [])).map((day, index) => (
                      <IonCard key={index} className="day-card">
                        <IonCardHeader>
                          <IonCardTitle>
                            {day.Day}: {day.Overview}
                          </IonCardTitle>
                          {day.EatingWindow && (
                            <div style={{ marginTop: '8px', fontSize: '14px', color: colors.warmGray, fontStyle: 'italic' }}>
                              Eating Window: {day.EatingWindow}
                            </div>
                          )}
                          {day.DailyTotals && (
                            <div style={{ marginTop: '12px' }}>
                              <IonBadge color="secondary">
                                {day.DailyTotals.Calories || 0} kcal
                              </IonBadge>
                              <IonBadge color="tertiary" style={{ marginLeft: '4px' }}>
                                Protein: {day.DailyTotals.Protein_g || 0}g
                              </IonBadge>
                              <IonBadge color="tertiary" style={{ marginLeft: '4px' }}>
                                Carbs: {day.DailyTotals.Carbs_g || 0}g
                              </IonBadge>
                              <IonBadge color="tertiary" style={{ marginLeft: '4px' }}>
                                Fat: {day.DailyTotals.Fat_g || 0}g
                              </IonBadge>
                              <IonBadge color="tertiary" style={{ marginLeft: '4px' }}>
                                Fibre: {day.DailyTotals.Fiber_g || 0}g
                              </IonBadge>
                            </div>
                          )}
                        </IonCardHeader>
                        <IonCardContent>
                          <div className="day-meals">
                            {day.Meals && Array.isArray(day.Meals) && day.Meals.length > 0 ? (
                              day.Meals.map((meal, mealIndex) => (
                                <div key={mealIndex} className="meal-card" style={{ 
                                  marginBottom: mealIndex < day.Meals.length - 1 ? '16px' : '0',
                                  padding: '12px',
                                  backgroundColor: colors.rgba(colors.lightPurpleGray, 0.15),
                                  borderRadius: '8px',
                                  borderLeft: `4px solid ${colors.blueGray}`
                                }}>
                                  <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px',
                                    flexWrap: 'wrap',
                                    gap: '8px'
                                  }}>
                                    <span style={{ 
                                      fontSize: '16px', 
                                      fontWeight: '600',
                                      color: colors.darkGray
                                    }}>
                                      {meal.Meal}
                                    </span>
                                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                      <IonBadge color="light" style={{ fontSize: '11px' }}>
                                        {meal.Calories || 0} kcal
                                      </IonBadge>
                                      <IonBadge color="light" style={{ fontSize: '11px' }}>
                                        P: {meal.Protein_g || 0}g
                                      </IonBadge>
                                      <IonBadge color="light" style={{ fontSize: '11px' }}>
                                        C: {meal.Carbs_g || 0}g
                                      </IonBadge>
                                      <IonBadge color="light" style={{ fontSize: '11px' }}>
                                        F: {meal.Fat_g || 0}g
                                      </IonBadge>
                                      <IonBadge color="light" style={{ fontSize: '11px' }}>
                                        Fibre: {meal.Fiber_g || 0}g
                                      </IonBadge>
                                    </div>
                                  </div>
                                  <div style={{ 
                                    whiteSpace: 'pre-wrap', 
                                    lineHeight: '1.6',
                                    color: colors.darkGray,
                                    fontSize: '14px'
                                  }}>
                                    {meal.SampleMeal}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div style={{ color: colors.warmGray }}>
                                No meals available for this day
                              </div>
                            )}
                          </div>
                        </IonCardContent>
                      </IonCard>
                    ))}
                  </div>
                  </div>
                </div>
              )}

              {/* Prompts Toggle - Only show if prompts are available from backend */}
              {promptsUsed.system && promptsUsed.system.trim() !== '' && (
                <div className="prompts-section" style={{ marginTop: '32px' }}>
                  <IonButton
                    fill="clear"
                    expand="block"
                    onClick={() => setShowPrompts(!showPrompts)}
                    style={{ 
                      marginBottom: '16px',
                      '--color': colors.warmGray,
                      fontSize: '14px'
                    }}
                  >
                    <IonIcon 
                      icon={showPrompts ? chevronUpOutline : chevronDownOutline} 
                      style={{ marginRight: '8px' }}
                    />
                    {showPrompts ? 'Hide' : 'Show'} Prompts Used
                  </IonButton>

                  {showPrompts && (
                    <div className="prompts-display">
                      <IonCard>
                        <IonCardHeader>
                          <IonCardTitle>System Prompt</IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', overflow: 'auto' }}>
                            {promptsUsed.system}
                          </pre>
                        </IonCardContent>
                      </IonCard>
                      <IonCard style={{ marginTop: '16px' }}>
                        <IonCardHeader>
                          <IonCardTitle>User Prompt</IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', overflow: 'auto' }}>
                            {promptsUsed.user}
                          </pre>
                        </IonCardContent>
                      </IonCard>
                    </div>
                  )}
                </div>
              )}
            </IonCardContent>
          </IonCard>

          {/* Generation Modal */}
          <IonModal
            isOpen={showGenerationModal}
            onDidDismiss={() => setShowGenerationModal(false)}
            backdropDismiss={false}
            cssClass="modal-top-position"
          >
            <IonHeader>
              <IonToolbar>
                <IonTitle>Generating Your Plan</IonTitle>
              </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '60vh',
                padding: '40px 20px',
                textAlign: 'center'
              }}>
                <IonSpinner name="crescent" style={{ marginBottom: '24px', width: '48px', height: '48px' }} />
                <h2 style={{ 
                  marginBottom: '16px', 
                  color: colors.darkGray,
                  fontSize: '20px',
                  fontWeight: '600'
                }}>
                  Generating Your Diet Plan
                </h2>
                <p style={{ 
                  fontSize: '16px', 
                  color: colors.warmGray,
                  lineHeight: '1.6',
                  marginBottom: '12px'
                }}>
                  This may take a few minutes.
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  color: colors.warmGray,
                  lineHeight: '1.6'
                }}>
                  The plan will be emailed to you and the Member once complete.
                </p>
              </div>
            </IonContent>
          </IonModal>

          <IonAlert
            isOpen={showDeleteConfirm}
            onDidDismiss={() => setShowDeleteConfirm(false)}
            header="Delete Diet Plan"
            message="Are you sure you want to delete this plan? This action cannot be undone."
            buttons={[
              { text: 'Cancel', role: 'cancel' },
              {
                text: 'Delete',
                role: 'destructive',
                handler: () => {
                  setShowDeleteConfirm(false);
                  handleDeletePlan();
                }
              }
            ]}
          />

          {/* Toast Container */}
          <ToastContainer
            position="top-center"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </div>
  );

  if (noPageWrapper) {
    return <div className="diet-plan-content">{content}</div>;
  }

  return (
    <IonPage>
      <IonContent className="diet-plan-content">
        {content}
      </IonContent>
    </IonPage>
  );
};

export default DietGenerator;

